import DuoMascot from '@/components/v2/DuoMascot'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

interface Props {
  onPrepare?: () => Promise<{ success: boolean }>
  redirectTo?: string
}

/**
 * Duolingo's post-sign-up wait, in the funnel's loading style. The timing and
 * the `onPrepare`/`redirectTo` contract are the shared `PreparingPage`'s: the
 * optional prepare runs first, and three seconds after it resolves the page
 * moves on.
 */
export default function PreparingPage({
  onPrepare,
  redirectTo = '/checkout/plans',
}: Props = {}) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [isComplete, setIsComplete] = useState(!onPrepare)
  const [progress, setProgress] = useState(0)

  // The bar is cosmetic: it creeps to 90% while the prepare runs, then fills
  // over the three seconds before the page navigates away.
  useEffect(() => {
    if (isComplete) {
      setProgress(100)
      return
    }

    const interval = setInterval(
      () => setProgress((previous) => Math.min(previous + 1, 90)),
      40,
    )

    return () => clearInterval(interval)
  }, [isComplete])

  // Effect for running the optional prepare action
  useEffect(() => {
    if (!onPrepare) return

    const runPrepare = async () => {
      const { success } = await onPrepare()
      if (success) {
        setIsComplete(true)
      }
    }

    runPrepare()
  }, [onPrepare])

  // Effect for navigation after preparation is complete
  useEffect(() => {
    if (!isComplete) return

    const timeout = setTimeout(() => {
      navigate(redirectTo)
    }, 3000)

    return () => clearTimeout(timeout)
  }, [isComplete, navigate, onPrepare, redirectTo])

  return (
    <div className="min-h-dvh bg-white">
      <main className="duo-container flex min-h-dvh flex-col items-center justify-center gap-8 pt-6 pb-8">
        <DuoMascot className="h-28 w-28" />

        <div className="space-y-2 text-center">
          <h1 className="text-duo-ink text-[26px] leading-tight font-extrabold text-balance">
            {t('pages.signUp.loading.heading')}
          </h1>

          <p className="text-duo-muted text-[15px] leading-relaxed">
            {t('pages.signUp.loading.analyzingResponses')}
          </p>
        </div>

        <div className="bg-duo-line h-4 w-full max-w-xs overflow-hidden rounded-full">
          <div
            className="bg-duo-primary h-full rounded-full transition-[width] duration-200 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>
      </main>
    </div>
  )
}
